/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import {
    FooterBottomSection,
    footerContainerStyles,
    FooterContainerStyles,
    FooterContainerWrapper,
    FooterSiteMapLinksWrapper,
    FooterTopSection,
    GeneralLinksWrapper,
    SocialLinksWrapper,
    SubscribeSocialLinksWrapper
} from "@eisinc/Overrides/Widgets/Footer/FooterContainer.style";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import React, { FC } from "react";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    const copyrightMessage = () => {
        const message = (siteMessage("Custom_Footer_Copyright") as string) || "© {0} EIS Inc. All Rights Reserved.";
        return message.replace("{0}", String(new Date().getFullYear()));
    };

    return (
        <FooterContainerWrapper>
            <FooterTopSection>
                <GridContainer {...styles.widgetGridContainer}>
                    <GridItem {...styles.generalLinksGridItem}>
                        <GridContainer gap={0}>
                            <GridItem {...styles.linkList1GridItem}>
                                <GeneralLinksWrapper>
                                    <Zone zoneName="LinkList1" contentId={id} />
                                </GeneralLinksWrapper>
                            </GridItem>
                            <GridItem {...styles.linkList2GridItem}>
                                <GeneralLinksWrapper>
                                    <Zone zoneName="LinkList2" contentId={id} />
                                </GeneralLinksWrapper>
                            </GridItem>
                            <GridItem {...styles.linkList3GridItem}>
                                <GeneralLinksWrapper className="support">
                                    <Zone zoneName="LinkList3" contentId={id} />
                                </GeneralLinksWrapper>
                            </GridItem>
                            <GridItem {...styles.linkList4GridItem}>
                                <GeneralLinksWrapper className="business-unit">
                                    <Zone zoneName="LinkList4" contentId={id} />
                                </GeneralLinksWrapper>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.subscribeSocialLinksGridItem}>
                        <SubscribeSocialLinksWrapper>
                            <GridContainer gap={0}>
                                <GridItem {...styles.subscribeGridItem}>
                                    <Zone zoneName="Subscribe" contentId={id} />
                                </GridItem>
                                <GridItem {...styles.socialLinksGridItem}>
                                    <SocialLinksWrapper>
                                        <Zone zoneName="SocialLinks" contentId={id} />
                                    </SocialLinksWrapper>
                                </GridItem>
                            </GridContainer>
                        </SubscribeSocialLinksWrapper>
                    </GridItem>
                </GridContainer>
            </FooterTopSection>
            <FooterBottomSection>
                <GridContainer {...styles.widgetGridContainer}>
                    <GridItem {...styles.copyRightGridItem}>
                        <StyledWrapper {...styles.copyRight}>{copyrightMessage()}</StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.siteMapLinksGridItem}>
                        <FooterSiteMapLinksWrapper>
                            <Zone zoneName="RichContent" contentId={id} />
                        </FooterSiteMapLinksWrapper>
                    </GridItem>
                </GridContainer>
            </FooterBottomSection>
        </FooterContainerWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
